import type {
  AccountSeatOverview,
  GrantPersonAccessCommand,
  GrantPersonAccessResult,
  InviteAccountUserCommand,
  InviteAccountUserResult,
  PendingAccountInvitation,
  PersonSeatAssignment,
  RemovePersonSeatResult,
  RestorePersonSeatResult,
  ChangeAccountUserRoleCommand,
  ChangeAccountUserRoleResult,
} from "@/domain/model/AccountSeatOverview";
import type {
  GrantPersonAccessRequestDto,
  InviteRequestDto,
  InviteResponseDto,
  PendingAccountInvitationResponseDto,
  PersonSeatAssignmentResponseDto,
  RemovePersonFromSeatResponseDto,
  ResendInvitationResponseDto,
  RestorePersonSeatResponseDto,
  SeatOverviewResponseDto,
  ChangeAccountUserRoleDto,
  ChangeAccountUserRoleResultDto,
} from "../dto/AccountSeatDto";
import type { ResendAccountInvitationResult } from "@/domain/model/Invitation";
import { mapAccountUserRole, mapNullableAccountUserRole } from "./util";

function toDate(value: string | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function toRequiredDate(value: string | null, fieldName = "date"): Date {
  if (!value) {
    throw new Error(`Missing required ${fieldName}.`);
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    throw new Error(`Invalid required ${fieldName}: ${value}`);
  }

  return date;
}

function mapPersonSeatAssignmentDtoToModel(
  dto: PersonSeatAssignmentResponseDto,
): PersonSeatAssignment {
  return {
    personId: dto.personId,
    displayName: dto.displayName,
    preferredName: dto.preferredName,
    email: dto.email,
    userId: dto.userId,
    role: mapNullableAccountUserRole(dto.role),
    membershipStatus: dto.membershipStatus,
    state: dto.state,
    invitationId: dto.invitationId,
    invitationExpiresAt: toDate(dto.invitationExpiresAt),
  };
}

function mapPendingAccountInvitaionDtoToModel(
  dto: PendingAccountInvitationResponseDto
): PendingAccountInvitation {
  return {
    invitationId: dto.invitationId,
    email: dto.email,
    role: dto.role,
    personId: dto.personId,
    createdAt: toRequiredDate(dto.createdAt, "createdAt"),
    expiresAt: toRequiredDate(dto.expiresAt, "expiresAt")
  }
}

export function mapSeatOverviewResponseDtoToModel(
  dto: SeatOverviewResponseDto,
): AccountSeatOverview {
  return {
    accountId: dto.accountId,
    purchasedSeats: dto.purchasedSeats,
    activeSeats: dto.activeSeats,
    freeSeats: dto.freeSeats,
    persons: dto.persons.map(mapPersonSeatAssignmentDtoToModel),
    pendingInvitations: dto.pendingInvitations.map(mapPendingAccountInvitaionDtoToModel)
  };
}

export function mapInviteAccountUserCommandToDto(
  command: InviteAccountUserCommand,
): InviteRequestDto {
  return {
    email: command.email.trim(),
    role: command.role,
  };
}

export function mapInviteResponseDtoToResult(
  dto: InviteResponseDto,
): InviteAccountUserResult {
  return {
    email: dto.email,
    role: mapAccountUserRole(dto.role),
    personId: dto.personId,
    expiresAt: toRequiredDate(dto.expiresAt, "expiresAt"),
    acceptUrl: dto.acceptUrl
  };
}

export function mapGrantPersonAccessCommandToDto(
  command: GrantPersonAccessCommand,
): GrantPersonAccessRequestDto {
  return {
    email: command.email,
    role: command.role,
  };
}

export function mapGrantPersonAccessResponseDtoToResult(
  dto: InviteResponseDto,
): GrantPersonAccessResult {
  return {
    email: dto.email,
    role: mapAccountUserRole(dto.role),
    personId: dto.personId,
    expiresAt: toRequiredDate(dto.expiresAt, "expiresAt"),
    acceptUrl: dto.acceptUrl
  };
}

export function mapRemovePersonSeatResponseDtoToResult(
  dto: RemovePersonFromSeatResponseDto,
): RemovePersonSeatResult {
  return {
    personId: dto.personId,
    userId: dto.userId,
    role: mapAccountUserRole(dto.role),
    activeSeatsAfter: dto.activeSeatsAfter,
  };
}

export function mapRestorePersonSeatResponseDtoToResult(
  dto: RestorePersonSeatResponseDto,
): RestorePersonSeatResult {
  return {
    personId: dto.personId,
    userId: dto.userId,
    role: mapAccountUserRole(dto.role),
    activeSeatsAfter: dto.activeSeatsAfter,
  };
}

export function mapResendInvitationResponseDtoToResult(
  dto: ResendInvitationResponseDto,
): ResendAccountInvitationResult {
  return {
    invitationId: dto.invitationId,
    email: dto.email,
    role: mapAccountUserRole(dto.role),
    personId: dto.personId,
    expiresAt: new Date(dto.expiresAt),
  };
}

export function mapChangeAccountUserRoleCommandToDto(
  cmd: ChangeAccountUserRoleCommand,
): ChangeAccountUserRoleDto {
  return {
    userId: cmd.userId,
    desiredRole: cmd.desiredRole,
  };
}

export function mapChangeAccountUserRoleResultDtoToModel(
  dto: ChangeAccountUserRoleResultDto,
): ChangeAccountUserRoleResult {
  return {
    userId: dto.userId,
    role: mapAccountUserRole(dto.role),
  };
}
